import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FaArrowLeft, FaClock, FaUser, FaBoxOpen, FaPaperPlane, FaFileUpload, FaFilePdf, FaImage, FaDownload, FaCheckCircle, FaShippingFast, FaFileInvoice } from 'react-icons/fa';
import { toast } from 'sonner';

export default function SupplierQuoteThread() {
  const { API, user, BACKEND_URL } = useContext(AppContext);
  const { i18n, t } = useTranslation();
  const { quoteId } = useParams();
  const navigate = useNavigate();

  const [quote, setQuote] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const isInitialLoad = useRef(true);
  const previousMessageCount = useRef(0);
  
  const [form, setForm] = useState({
    content: '',
    offered_price: '',
    delivery_time: '',
  });
  
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchThread();
    const interval = setInterval(fetchThread, 5000); // Poll every 5s
    return () => clearInterval(interval);
  }, [user, quoteId]);

  useEffect(() => {
    // Only scroll to bottom on initial load or when new messages are added
    if (isInitialLoad.current && messages.length > 0) {
      scrollToBottom();
      isInitialLoad.current = false;
      previousMessageCount.current = messages.length;
    } else if (messages.length > previousMessageCount.current) {
      // New message added
      scrollToBottom();
      previousMessageCount.current = messages.length;
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchThread = async () => {
    try {
      const res = await axios.get(`${API}/quotes/${quoteId}/thread?user_id=${user.id}`);
      setQuote(res.data.quote);
      
      const newMessages = res.data.messages || [];
      setMessages(prev => {
        if (JSON.stringify(prev) !== JSON.stringify(newMessages)) {
          return newMessages;
        }
        return prev;
      });

      if (loading) setLoading(false);
    } catch (e) {
      console.error('Error loading quote thread', e);
      if (loading) {
        toast.error(i18n.language === 'fr' ? "Impossible de charger cette proposition." : 'Unable to load this proposal.');
        navigate('/supplier/opportunities');
      }
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) return;

    setSending(true);
    try {
      const payload = {
        content: form.content.trim(),
        offered_price: form.offered_price ? parseFloat(form.offered_price) : null,
        delivery_time: form.delivery_time || null,
      };
      const res = await axios.post(
        `${API}/quotes/${quoteId}/messages?user_id=${user.id}`,
        payload
      );
      setMessages((prev) => [...prev, res.data]);
      setForm({ content: '', offered_price: '', delivery_time: '' });
      fetchThread(); // Refresh quote status
    } catch (e) {
      console.error('Error sending message', e);
      const msg = e.response?.data?.detail || (i18n.language === 'fr' ? "Erreur lors de l'envoi du message." : 'Error while sending message.');
      // Safety check: ensure msg is a string before rendering in toast
      toast.error(typeof msg === 'string' ? msg : 'Erreur (détails console)');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Increased limit to 50MB for mobile uploads
    if (file.size > 50 * 1024 * 1024) {
      toast.error(i18n.language === 'fr' ? 'Fichier trop volumineux (max 50MB)' : 'File too large (max 50MB)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // 1. Upload
      const uploadRes = await axios.post(`${API}/quotes/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fileData = uploadRes.data; // { url, name, type, size }
      
      // 2. Send message with attachment
      const payload = {
        content: i18n.language === 'fr' ? `Pièce jointe : ${fileData.name}` : `Attachment: ${fileData.name}`,
        attachments: [fileData]
      };
      
      await axios.post(`${API}/quotes/${quoteId}/messages?user_id=${user.id}`, payload);
      toast.success(i18n.language === 'fr' ? 'Fichier envoyé' : 'File sent');
      fetchThread();
    } catch (error) {
      console.error('Error uploading file:', error);
      const msg = error.response?.data?.detail || (i18n.language === 'fr' ? 'Erreur lors de l\'envoi du fichier' : 'Error uploading file');
      // Safety check: ensure msg is a string before rendering in toast
      toast.error(typeof msg === 'string' ? msg : 'Erreur upload (détails console)');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmShipping = async () => {
    if (!confirm(i18n.language === 'fr' ? 'Confirmer la préparation et l\'expédition ?' : 'Confirm preparation and shipping?')) return;
    
    try {
      await axios.post(`${API}/quotes/${quoteId}/confirm-shipping?user_id=${user.id}`);
      toast.success(i18n.language === 'fr' ? 'Expédition confirmée' : 'Shipping confirmed');
      fetchThread();
    } catch (error) {
      console.error('Error confirming shipping:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API}/quotes/${quoteId}/generate-pdf?user_id=${user.id}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `devis_${quoteId.slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(i18n.language === 'fr' ? 'Devis téléchargé' : 'Quote downloaded');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors du téléchargement' : 'Download error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  if (!quote) return null;

  const isVendor = user && quote.vendor_id === user.id;
  if (!isVendor) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">Access Denied</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => navigate('/supplier/product-quote-requests')}
            className="flex items-center gap-2 text-[#0A2540] hover:underline"
          >
            <FaArrowLeft />
            {i18n.language === 'fr' ? 'Retour' : 'Back'}
          </button>
          
          <div className="flex items-center gap-3">
            {/* Download PDF Button */}
            <Button 
              onClick={handleDownloadPdf} 
              disabled={downloadingPdf}
              variant="outline"
              className="border-[#635BFF] text-[#635BFF] hover:bg-[#635BFF]/10"
            >
              {downloadingPdf ? (
                <span className="animate-pulse">{i18n.language === 'fr' ? 'Génération...' : 'Generating...'}</span>
              ) : (
                <>
                  <FaFileInvoice className="mr-2" />
                  {i18n.language === 'fr' ? 'Télécharger Devis PDF' : 'Download PDF Quote'}
                </>
              )}
            </Button>
            
            {quote.status === 'accepted' && (
              <Button onClick={handleConfirmShipping} className="bg-blue-600 hover:bg-blue-700">
                <FaShippingFast className="mr-2" />
                {i18n.language === 'fr' ? 'Confirmer Expédition' : 'Confirm Shipping'}
              </Button>
            )}
            
            {quote.status === 'shipped' && (
              <div className="bg-[#635BFF]/10 text-[#5248d9] px-4 py-2 rounded-lg flex items-center">
                <FaCheckCircle className="mr-2" />
                {i18n.language === 'fr' ? 'Expédié' : 'Shipped'}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quote summary */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>{i18n.language === 'fr' ? 'Détails de la demande' : 'Request details'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">{i18n.language === 'fr' ? 'Produit' : 'Product'}</p>
                  <div className="flex items-center gap-2 font-semibold text-[#0A2540]">
                    <FaBoxOpen /> {quote.product_name}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{i18n.language === 'fr' ? 'Quantité' : 'Quantity'}</p>
                  <p className="font-semibold">{quote.quantity}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{i18n.language === 'fr' ? 'Client' : 'Client'}</p>
                  <div className="flex items-center gap-2">
                    <FaUser className="text-gray-400" />
                    <span>{quote.user_name}</span>
                  </div>
                  <p className="text-xs text-gray-400 ml-6">{quote.user_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{i18n.language === 'fr' ? 'Statut' : 'Status'}</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    quote.status === 'accepted' ? 'bg-[#635BFF]/10 text-[#5248d9]' :
                    quote.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {quote.status.toUpperCase()}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Conversation */}
          <div className="lg:col-span-2">
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center justify-between">
                  <span>{i18n.language === 'fr' ? 'Discussion' : 'Discussion'}</span>
                  {uploading && <span className="text-xs text-blue-500 animate-pulse">Upload...</span>}
                </CardTitle>
              </CardHeader>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {quote.message && (
                  <div className="flex flex-col items-start">
                    <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 max-w-[80%] shadow-sm">
                      <p className="text-sm text-gray-800">{quote.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {i18n.language === 'fr' ? 'Message initial' : 'Initial message'}
                      </p>
                    </div>
                  </div>
                )}

                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                      <div className={`rounded-lg px-4 py-3 max-w-[80%] text-sm shadow-sm ${
                        mine ? 'bg-[#0A2540] text-white' : 'bg-white border border-gray-200 text-gray-800'
                      }`}>
                        <p className="whitespace-pre-wrap">{m.content}</p>
                        
                        {/* Attachments */}
                        {m.attachments && m.attachments.map((att, idx) => (
                          <a 
                            key={idx} 
                            href={`${BACKEND_URL}${att.url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`flex items-center gap-2 mt-2 p-2 rounded ${
                              mine ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                          >
                            {att.type === 'pdf' ? <FaFilePdf /> : <FaImage />}
                            <span className="truncate max-w-[150px]">{att.name}</span>
                            <FaDownload className="ml-auto" />
                          </a>
                        ))}

                        {/* Quote Details */}
                        {(m.offered_price || m.delivery_time) && (
                          <div className={`mt-2 pt-2 border-t ${mine ? 'border-white/20' : 'border-gray-200'}`}>
                            {m.offered_price && (
                              <div>
                                {i18n.language === 'fr' ? 'Prix : ' : 'Price: '}
                                <strong>{m.offered_price} €</strong>
                              </div>
                            )}
                            {m.delivery_time && (
                              <div>
                                {i18n.language === 'fr' ? 'Délai : ' : 'Time: '}
                                <strong>{m.delivery_time}</strong>
                              </div>
                            )}
                          </div>
                        )}
                        
                        <p className={`text-[10px] mt-1 ${mine ? 'text-blue-200' : 'text-gray-400'}`}>
                          {formatDate(m.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 bg-white border-t">
                <form onSubmit={handleSend} className="space-y-3">
                  <div className="flex gap-2">
                    <label className="cursor-pointer p-2 text-gray-500 hover:text-[#0A2540]">
                      <input 
                        type="file" 
                        accept="image/*,application/pdf,.heic,.heif"
                        className="hidden" 
                        onChange={handleFileUpload} 
                        disabled={uploading} 
                      />
                      <FaFileUpload size={20} />
                    </label>
                    <Textarea
                      value={form.content}
                      onChange={(e) => setForm({ ...form, content: e.target.value })}
                      rows={1}
                      placeholder={i18n.language === 'fr' ? 'Écrire un message...' : 'Write a message...'}
                      className="flex-1 min-h-[40px] resize-none"
                    />
                    <Button type="submit" disabled={sending || !form.content.trim()} className="bg-[#635BFF]">
                      <FaPaperPlane />
                    </Button>
                  </div>
                  
                  {/* Price inputs only for vendor */}
                  <div className="flex gap-4 pt-2 border-t">
                    <div className="flex-1">
                      <input
                        type="number"
                        placeholder={i18n.language === 'fr' ? 'Prix (€)' : 'Price (€)'}
                        className="w-full text-sm border rounded px-2 py-1"
                        value={form.offered_price}
                        onChange={(e) => setForm({ ...form, offered_price: e.target.value })}
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder={i18n.language === 'fr' ? 'Délai' : 'Lead time'}
                        className="w-full text-sm border rounded px-2 py-1"
                        value={form.delivery_time}
                        onChange={(e) => setForm({ ...form, delivery_time: e.target.value })}
                      />
                    </div>
                  </div>
                </form>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
